import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLicensePlateInfo } from '../../../util/api';
import { SET_LICENSE_PLATE_INFO } from '../../../ducks/currentLicensePlate.duck';
import { checkWheelFitment } from '../../../util/wheelFitment';
import { boltCircleText } from '../../../util/boltCircleCore';
import { RegNumInput } from '../../WP/RegNumInput';

import css from './WheelFitmentCheck.module.css';

const REG_STORAGE_KEY = 'wheelplace.regNumber';
const SERIES_STORAGE_KEY = 'wheelplace.regSeries';

const readStoredReg = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(REG_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

const readStoredSeries = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(SERIES_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

const SpecRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className={css.specRow}>
      <span className={css.specRowLabel}>{label}</span>
      <span className={css.specRowValue}>{value}</span>
    </div>
  );
};

const ResultBanner = ({ result }) => {
  const cls =
    result.compatible === true
      ? css.bannerSuccess
      : result.compatible === false
        ? css.bannerWarning
        : css.bannerNeutral;
  const heading =
    result.compatible === true
      ? 'Bra matchning!'
      : result.compatible === false
        ? 'Passar troligtvis inte'
        : 'Osäker matchning';
  return (
    <div className={`${css.banner} ${cls}`}>
      <div>
        <p className={css.bannerHeading}>{heading}</p>
        <p className={css.bannerBody}>{result.summary}</p>
      </div>
    </div>
  );
};

const CompareRow = ({ label, listingValue, carValue, status }) => {
  if (!listingValue) return null;
  const hasCar = !!carValue;
  return (
    <div
      className={`${css.compareRow} ${hasCar ? '' : css.compareRowSingle}`}
      data-status={hasCar ? status : null}
    >
      <div className={css.compareLabel}>{label}</div>
      <div className={css.compareCol}>
        <span className={css.compareSide}>Annons</span>
        <span className={css.compareText}>{listingValue}</span>
      </div>
      {hasCar ? (
        <div className={css.compareCol}>
          <span className={css.compareSide}>Din bil</span>
          <span className={css.compareText}>{carValue}</span>
        </div>
      ) : null}
    </div>
  );
};

const WheelFitmentCheck = ({ listing }) => {
  const dispatch = useDispatch();
  const currentLicensePlate = useSelector(
    (state) => state.currentLicensePlate?.currentLicensePlate
  );
  const currentUser = useSelector((state) => state.user?.currentUser);

  const publicData = listing?.attributes?.publicData || {};
  const userPublicData = currentUser?.attributes?.profile?.publicData || {};

  const regFromUser = userPublicData.regNumber || null;
  const seriesFromUser = userPublicData.regSeries || null;
  const reg = regFromUser || readStoredReg();
  const series = seriesFromUser || readStoredSeries();

  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(false);
  const [inlineReg, setInlineReg] = useState('');

  useEffect(() => {
    let cancelled = false;
    const haveCar = !!currentLicensePlate;
    if (haveCar || !reg) return;

    const cleanReg = reg.replace(/[\s-]/g, '');
    setBootstrapping(true);
    setBootstrapError(false);
    getLicensePlateInfo(series ? { licensePlate: cleanReg, selectedSeries: series } : { licensePlate: cleanReg })
      .then((data) => {
        if (cancelled) return;
        if (data && data.status !== 'choose_series' && data.status !== 'choose_Modal') {
          dispatch({ type: SET_LICENSE_PLATE_INFO, payload: data, reg: cleanReg });
        }
      })
      .catch(() => {
        if (!cancelled) setBootstrapError(true);
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reg, series, currentLicensePlate, dispatch]);

  const { tireWidthFront, profileFront, diameter, numberOfBolts, boltCircle } = publicData;
  const dimensions =
    tireWidthFront && profileFront && diameter
      ? `${tireWidthFront}/${profileFront}R${diameter}`
      : diameter
        ? `${diameter}"`
        : null;
  // Console's key "1397" is 139.7 (P25009-488).
  const boltPattern =
    numberOfBolts && boltCircle
      ? `${numberOfBolts}/${boltCircleText(boltCircle) || boltCircle}`
      : null;

  const result = currentLicensePlate
    ? checkWheelFitment({
        listingPublicData: publicData,
        licensePlateInfo: currentLicensePlate,
        hasSeries: !!series,
      })
    : null;

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.headerIcon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 6h13M3 6l3-3M3 6l3 3M21 18H8M21 18l-3-3M21 18l-3 3"
            />
          </svg>
        </div>
        <div className={css.headerText}>
          <h3>Kolla passformen</h3>
          <p>Vi jämför annonsens specifikationer mot din bil.</p>
        </div>
      </div>

      {result ? (
        <>
          <ResultBanner result={result} />
          {result.carDetected ? <p className={css.carDetected}>Bil: {result.carDetected}</p> : null}
          {(() => {
            const findSpec = (label) => result.carSpecs?.find((s) => s.label === label);
            const valueOrNull = (row) => (row && row.value !== '—' ? row.value : null);

            // Prefer tire-dim row (full 235/45R18) but fall back to rim diameter
            // when the listing has no tire width/profile — compareTire returns null in that case.
            const dimRow = findSpec('Däckdimension') || findSpec('Fälgdiameter');
            const boltsRow = findSpec('Bultmönster (antal)');
            const pcdRow = findSpec('PCD');

            const carBoltsVal = valueOrNull(boltsRow);
            const carPcdVal = valueOrNull(pcdRow);
            const carBoltPattern =
              carBoltsVal && carPcdVal
                ? `${carBoltsVal}/${String(carPcdVal).replace(' mm', '')}`
                : null;
            // Combined status: any mismatch is a mismatch; both match → match; else uncertain.
            const combinedBoltStatus =
              boltsRow?.status === 'mismatch' || pcdRow?.status === 'mismatch'
                ? 'mismatch'
                : boltsRow?.status === 'match' && pcdRow?.status === 'match'
                  ? 'match'
                  : 'uncertain';

            return (
              <div className={css.compareTable}>
                <CompareRow
                  label="Dimension"
                  listingValue={dimensions}
                  carValue={valueOrNull(dimRow)}
                  status={dimRow?.status || null}
                />
                <CompareRow
                  label="Bultmönster"
                  listingValue={boltPattern}
                  carValue={carBoltPattern}
                  status={combinedBoltStatus}
                />
              </div>
            );
          })()}
        </>
      ) : bootstrapping ? (
        <>
          <div className={css.specGrid}>
            <SpecRow label="Dimension" value={dimensions} />
            <SpecRow label="Bultmönster" value={boltPattern} />
          </div>
          <p className={css.muted}>Hämtar bildata…</p>
        </>
      ) : reg && bootstrapError ? (
        <>
          <div className={css.specGrid}>
            <SpecRow label="Dimension" value={dimensions} />
            <SpecRow label="Bultmönster" value={boltPattern} />
          </div>
          <p className={css.muted}>Kunde inte hämta bildata för {reg}.</p>
        </>
      ) : (
        <>
          <div className={css.specGrid}>
            <SpecRow label="Dimension" value={dimensions} />
            <SpecRow label="Bultmönster" value={boltPattern} />
          </div>
          <div className={css.emptyState}>
            <p className={css.emptyText}>
              Ange ditt registreringsnummer för att se om dessa hjul passar din bil.
            </p>
            <div className={css.inlineRegWrap}>
              <RegNumInput
                size="compact"
                page="listingPage"
                savedRegNum={inlineReg}
                changeRegNum={setInlineReg}
                updateState={() => {
                  /* no-op: WheelFitmentCheck reads currentLicensePlate from Redux,
                     which RegNumInput dispatches to on success */
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WheelFitmentCheck;
